import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { cn } from "@shadcn/lib/utils";
import { Button } from "@shadcn/ui/button";
import { useNavigate } from "@tanstack/react-router";
import { capitalize } from "es-toolkit";
import { type ComponentProps, useEffect, useState } from "react";
import { HomeCardSections } from "./HomeCardSections";
import { useHomePageData } from "./HomePageData";
import { ScanQR } from "./ScanQR";
import { HomeSuggestionsSection } from "./HomeSuggestionsSection";
import { VideoGuideDrawer } from "./VideoGuideDrawer";
import { useHubSession } from "@contexts/HubSessionContext";
import { AppHeader } from "./layout/AppHeader";
import { HubMap } from "./layout/HubMap/HubMap";
import type { HubFilterService as Service } from "./layout/HubMap/hubFilters";
import { PageContainer } from "./layout/PageContainer";

const rafflePromoBackground = "/consumer-home-assets/raffle-promo-banner.webp";
const desktopHomeShellClass = "lg:mx-auto lg:w-[calc(100vw-32rem)] lg:min-w-[512px] lg:max-w-[752px]";

type HomePageProps = {
	scannedHub?: {
		id: string;
		name: string;
	} | null;
};

export const HomePage = (props: HomePageProps) => {
	const navigate = useNavigate();
	const { hub: sessionHub, setHub, clearHub } = useHubSession();
	// On the root home route no hub prop is passed; fall back to the stored hub session
	// so an already-selected hub is used everywhere without asking to reselect.
	const storedHub = props.scannedHub ?? (sessionHub ? { id: sessionHub.id, name: sessionHub.name } : null);
	const { LL } = useI18nContext();
	const i18n = LL["new-ui"];
	const [showHubMap, setShowHubMap] = useState(false);
	const [showStickyScanCTA, setShowStickyScanCTA] = useState(true);
	const [filterService, setFilterService] = useState<Service | null>(null);
	// The card that opened the hub map, e.g. goods reception, which shares the Lock B2B filter with Swap.
	const [targetPath, setTargetPath] = useState<string | null>(null);
	const {
		scannedHubServices,
		isScannedHubResolved,
		availableServices,
		hasManagedCompartmentAccess,
		hasWarenannahmeAccess,
		hasOneDeliveryAccess,
	} = useHomePageData(storedHub?.id);
	const scannedHub = isScannedHubResolved ? storedHub : null;

	useEffect(() => {
		if (sessionHub && !isScannedHubResolved) clearHub();
	}, [clearHub, isScannedHubResolved, sessionHub]);

	useEffect(() => {
		const handleScroll = () => {
			setShowStickyScanCTA(window.scrollY <= 24);
		};

		handleScroll();
		window.addEventListener("scroll", handleScroll, { passive: true });

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	const hasRaffle = scannedHubServices?.includes("RAFFLE") ?? false;
	const isLadoHub = scannedHub?.name.toLowerCase() === "lado";
	const isLuckyHub = scannedHub?.name.toLowerCase() === "lucky";

	const changeHubAction: ComponentProps<typeof HubMap>["renderAction"] = ({ id, name }, close) => (
		<Button
			className="w-full"
			onClick={() => {
				setHub({ id, name, side: undefined });
				close();

				if (filterService === "INSTANT_BUY") {
					navigate({
						to: "/$hubMetaName/instant-buy",
						params: { hubMetaName: name },
						search: { tab: "recommended" },
					});
					return;
				}

				if (filterService === "SHOP_N_COLLECT") {
					navigate({
						to: "/$hubMetaName/shop",
						params: { hubMetaName: name },
					});
					return;
				}

				if (targetPath === "/$hubMetaName/warenannahme") {
					navigate({
						to: "/$hubMetaName/warenannahme",
						params: { hubMetaName: name },
					});
					return;
				}

				if (filterService === "LOCK_B2B") {
					navigate({
						to: "/$hubMetaName/swap",
						params: { hubMetaName: name },
					});
					return;
				}

				if (filterService === "LOCK_B2C") {
					navigate({
						to: "/$hubMetaName/lock",
						params: { hubMetaName: name },
					});
					return;
				}

				navigate({
					to: "/$hubMetaName",
					params: { hubMetaName: name },
				});
			}}
		>
			{i18n.CHOOSE_HUB()}
		</Button>
	);

	const openHubMap = (service?: Service, to?: string) => {
		setShowHubMap(true);
		setFilterService(service ?? null);
		setTargetPath(to ?? null);
	};

	return (
		<PageContainer
			reserveFooterSpace={false}
			footerStyles={cn(
				"fixed bottom-0 left-0 right-0 transition-all duration-300 lg:hidden",
				showStickyScanCTA ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-8 opacity-0"
			)}
			footer={
				scannedHub ? null : (
					<div className="relative px-6 pb-6">
						<div className="pointer-events-none absolute inset-x-[-24px] bottom-0 h-28 translate-y-6 bg-gradient-to-t from-background via-background/95 via-30% to-transparent" />
						<ScanQR buttonClassName="relative h-10 bg-primary text-primary-foreground shadow-[0_20px_25px_rgba(0,0,0,0.1),0_8px_10px_rgba(0,0,0,0.1)] hover:bg-primary" />
					</div>
				)
			}
		>
			<AppHeader service="ALL" />

			<div className={desktopHomeShellClass}>
				<div className="px-6 pt-6">
					<div className="relative h-36 overflow-hidden rounded-xl p-4 lg:h-56">
						<img
							src="/consumer-home-assets/hero-bace-services.webp"
							alt=""
							className="absolute left-0 top-[-24.74%] h-[181.9%] w-full max-w-none"
						/>
						<div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/40 to-black/10" />
						{scannedHub ? (
							<div className="relative flex h-full flex-col justify-between text-white">
								<p className="max-w-xs font-breit text-[20px] font-medium leading-7 tracking-tight lg:max-w-full lg:text-[30px] lg:leading-none">
									{capitalize(scannedHub.name)}
								</p>
								<Button
									className="w-fit bg-white font-breit font-medium text-sm leading-5 text-foreground hover:bg-white/95"
									variant="outline"
									onClick={() => openHubMap()}
								>
									{i18n.CHANGE_HUB()}
								</Button>
							</div>
						) : (
							<div className="relative flex h-full flex-col justify-between text-white">
								<p className="block max-w-xs font-breit text-[20px] font-medium leading-7 tracking-tight lg:max-w-full lg:text-[30px] lg:leading-none">
									{i18n.HOME_HERO_TITLE()}
								</p>
								<VideoGuideDrawer
									trigger={
										<span className="text-sm underline underline-offset-2 lg:text-lg lg:leading-7">{i18n.HOME_HERO_SUBTITLE()}</span>
									}
								/>
							</div>
						)}
					</div>
				</div>

				{isLadoHub || isLuckyHub ? (
					<div className="px-6 pt-4">
						<a
							href={isLuckyHub ? "https://form.typeform.com/to/eZyGJwQO" : "https://form.typeform.com/to/axUsdD6k"}
							target="_blank"
							rel="noopener noreferrer"
							className="relative block min-h-28 w-full overflow-hidden rounded-[14px] px-4 py-4 text-left"
						>
							<img src="/consumer-home-assets/lado-survey-banner.webp" alt="" className="absolute inset-0 h-full w-full object-cover" />
							<div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
							<div className="relative max-w-[260px] text-white">
								<p className="font-breit text-xl font-medium leading-7">
									{isLuckyHub ? <T>{i18n.HOME_LUCKY_SURVEY_TITLE()}</T> : "Zur Umfrage"}
								</p>
								<p className="mt-1 text-sm leading-5">
									{isLuckyHub ? (
										<T>{i18n.HOME_LUCKY_SURVEY_DESCRIPTION()}</T>
									) : (
										"Sag uns in 5 Minuten, was du dir an diesem Standort wirklich wünschst, und sichere dir eine exklusive bace Bag."
									)}
								</p>
							</div>
						</a>
					</div>
				) : null}

				<HomeSuggestionsSection
					onSelectHub={openHubMap}
					scannedHub={scannedHub}
					scannedHubServices={scannedHubServices}
					availableServices={availableServices}
				/>

				{scannedHub && hasRaffle ? (
					<div className="px-6 pt-4">
						<button
							type="button"
							className="relative block min-h-28 w-full overflow-hidden rounded-[14px] px-4 py-4 text-left"
							onClick={() =>
								navigate({
									to: "/$hubMetaName/raffle",
									params: { hubMetaName: scannedHub.name },
								})
							}
						>
							<img src={rafflePromoBackground} alt="" className="absolute inset-0 h-full w-full object-cover" />
							<div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
							<div className="relative max-w-[220px] text-white">
								<p className="font-breit text-[1.75rem] leading-7 tracking-tight">{i18n.RAFFLE_PLAY_BUTTON()}</p>
								<p className="mt-2 text-sm leading-5">{i18n.RAFFLE_DESCRIPTION()}</p>
							</div>
						</button>
					</div>
				) : null}

				<HomeCardSections
					onSelectHub={openHubMap}
					scannedHub={scannedHub}
					scannedHubServices={scannedHubServices}
					hasManagedCompartmentAccess={hasManagedCompartmentAccess}
					hasWarenannahmeAccess={hasWarenannahmeAccess}
					hasOneDeliveryAccess={hasOneDeliveryAccess}
				/>

				<div className="px-6 pb-6 pt-6">
					<button
						type="button"
						className="relative flex h-36 w-full items-start justify-start overflow-hidden rounded-xl p-4 text-left lg:h-56"
						onClick={() => openHubMap()}
					>
						<img src="/consumer-home-assets/cta-discover-locations.webp" alt="" className="absolute inset-0 h-full w-full object-cover" />
						<div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-black/20" />
						<p className="relative max-w-xs font-breit text-xl font-medium leading-7 tracking-tight text-white lg:max-w-[209px] lg:text-[30px] lg:leading-none">
							{i18n.HOME_LOCATIONS()}
						</p>
					</button>
				</div>

				{!scannedHub ? (
					<div className="px-6 pb-6">
						<ScanQR buttonClassName="h-10 bg-primary text-primary-foreground shadow-[0_20px_25px_rgba(0,0,0,0.1),0_8px_10px_rgba(0,0,0,0.1)] hover:bg-primary" />
					</div>
				) : null}
			</div>

			<HubMap
				open={showHubMap}
				onOpenChange={setShowHubMap}
				currentHubMetaName={scannedHub?.name ?? null}
				renderAction={changeHubAction}
				filterService={filterService}
			/>
		</PageContainer>
	);
};
